import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export type HangoutEnv = 'staging' | 'prod' | 'preview';

const ORIGINS: Record<HangoutEnv, RegExp> = {
  staging: /^https:\/\/staging\.hang\.fm$/,
  prod: /^https:\/\/hang\.fm$/,
  preview: /^https:\/\/([a-z0-9-]+)-bringbackturntablefm\.vercel\.app$/,
};

const URLS: Record<HangoutEnv, string> = {
  staging: 'https://staging.hang.fm/napster',
  prod: 'https://hang.fm/napster',
  preview:
    'https://your-preview-subdomain-bringbackturntablefm.vercel.app/napster',
};

type HangoutAction =
  | { action: 'authenticate'; params: { token: string } }
  | { action: 'playTrack'; params: { token: string; trackId: string } }
  | { action: 'playPlaylist'; params: { token: string; playlistId: string } }
  | { action: 'joinRoom'; params: { token: string; room: string } };

type ReadyEvent = { type: 'ready' };

export type HangoutPublicAPI = {
  playTrack: (trackId: string) => void;
  playPlaylist: (playlistId: string) => void;
  joinRoom: (room: string) => void;
};

interface Props {
  env?: HangoutEnv;
  userToken: string;
  className?: string;
}

const HangoutNapsterEmbed = forwardRef<HangoutPublicAPI, Props>(
  function HangoutNapsterEmbed({ env = 'staging', userToken, className }, ref) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isReady, setIsReady] = useState(false);

    const src = useMemo(() => URLS[env], [env]);
    const originMatcher = useMemo(() => ORIGINS[env], [env]);

    const postToHangout = useCallback(
      (msg: HangoutAction) => {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) return;
        const targetOrigin = src.split('/napster')[0];
        iframe.contentWindow.postMessage(msg, targetOrigin);
      },
      [src],
    );

    useEffect(() => {
      const onMessage = (evt: MessageEvent) => {
        console.log('EVENT FROM IFRAME ', evt);
        if (!originMatcher.test(evt.origin)) return;
        const data = evt.data as ReadyEvent | unknown;
        if (data === 'ready') {
          console.log('READY ', userToken);
          setIsReady(true);
          if (userToken) {
            postToHangout({
              action: 'authenticate',
              params: { token: userToken },
            });
          }
        }
      };
      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }, [originMatcher, userToken, postToHangout]);

    useImperativeHandle(
      ref,
      () => ({
        playTrack: (trackId: string) => {
          if (!isReady || !userToken) return;
          postToHangout({
            action: 'playTrack',
            params: { token: userToken, trackId },
          });
        },
        playPlaylist: (playlistId: string) => {
          if (!isReady || !userToken) return;
          postToHangout({
            action: 'playPlaylist',
            params: { token: userToken, playlistId },
          });
        },
        joinRoom: (room: string) => {
          if (!isReady || !userToken) return;
          postToHangout({
            action: 'joinRoom',
            params: { token: userToken, room },
          });
        },
      }),
      [isReady, postToHangout, userToken],
    );

    return (
      <div className={className}>
        <iframe
          ref={iframeRef}
          src={src}
          title="Hangout Napster"
          allow="autoplay; encrypted-media"
          style={{
            width: '100%',
            height: 'calc(100vh - 100px)',
            border: 0,
            borderRadius: 12,
          }}
        />
      </div>
    );
  },
);

export default HangoutNapsterEmbed;
